/* eslint-disable max-lines */
import { useMemo, useCallback } from "react";
import { Badge, Progress, Stack, Typography } from "@altimateai/lego";
import { formatNumber } from "@uicore";
import {
  FilterType,
  createAPIDropdownFilter,
  parseSortDirection,
  type TableFilter,
  type TableHeaderType,
} from "@/components/tables/types";
import { useAppState } from "@/modules/app/useAppContext";
import { cn } from "@/lib/utils";
import { formatCost } from "@/helpers/utils";
import {
  COMPUTE_TYPE_FILTER_OPTIONS,
  PHOTON_FILTER_OPTIONS,
  getComputeTypeLabel,
} from "../helpers";
import type { ClusterFilterOptions, ClusterSummary } from "../types";
import { toOptions } from "@/pages/databricks/helpers";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { getClusterFilters } from "@/helpers/apis";

const CLUSTER_FILTER_PAGE_SIZE = 20;

const EMPTY_CLUSTER_FILTER_OPTIONS: ClusterFilterOptions = {
  cluster_names: [],
  workspaces: [],
  sources: [],
  driver_nodes: [],
  worker_nodes: [],
};

const buildStaticFilterConfig = (
  label: string,
  options: { label: string; value: string }[],
  value: string[],
  searchKey: string,
  onChange: (key: string, value: string[]) => void
): TableFilter | undefined =>
  options.length > 0
    ? ({
        filterType: FilterType.DROPDOWN,
        label,
        options,
        value,
        searchKey,
        onChange: (v: unknown) => onChange(searchKey, v as string[]),
      } as TableFilter)
    : undefined;

const buildClusterFiltersApiConfig = (
  label: string,
  value: string[],
  searchKey: "clusterName" | "workspace",
  optionKey: "cluster_names" | "workspaces",
  workspaceId: string | undefined,
  computeType: string[],
  onChange: (key: string, value: string[]) => void
): TableFilter =>
  createAPIDropdownFilter<ClusterFilterOptions>({
    filterType: FilterType.API_DROPDOWN,
    label,
    searchKey,
    value,
    pageSize: CLUSTER_FILTER_PAGE_SIZE,
    apiFunction: async ({ prefix, page, size }) =>
      (await getClusterFilters({
        workspace_id: workspaceId,
        prefix: prefix || undefined,
        page,
        size: size ?? CLUSTER_FILTER_PAGE_SIZE,
        compute_type: computeType.length > 0 ? computeType : undefined,
      })) ?? EMPTY_CLUSTER_FILTER_OPTIONS,
    extractorFunction: data => toOptions(data?.[optionKey]),
    onChange: selected => onChange(searchKey, selected),
  });

interface UseClusterListHeadersOptions {
  workspaceId?: string;
}

const useClusterListHeaders = ({ workspaceId }: UseClusterListHeadersOptions = {}) => {
  const { currency } = useAppState();
  const { searchParams, updateSearchParamsByKey } = useFiltersBySearchParams();

  // getAll() returns a fresh array each render; memoize so the `headers`
  // useMemo below (which depends on these) isn't rebuilt on every render.
  const clusterNameFilter = useMemo(() => searchParams.getAll("clusterName"), [searchParams]);
  const workspaceFilter = useMemo(() => searchParams.getAll("workspace"), [searchParams]);
  const computeTypeFilter = useMemo(() => searchParams.getAll("computeType"), [searchParams]);
  // Sanitize to the two known values so a stale / hand-edited URL
  // (e.g. ?enablePhoton=1) can't coerce to a silent "Photon off" filter and
  // so the dropdown only ever shows a value it actually offers.
  const rawEnablePhoton = searchParams.get("enablePhoton");
  const enablePhotonFilter =
    rawEnablePhoton === "true" || rawEnablePhoton === "false" ? rawEnablePhoton : "";

  const sortAttribute = searchParams.get("sortAttribute") || undefined;
  const sortOrder = parseSortDirection(searchParams.get("sortOrder")) || undefined;

  const buildSortConfig = useCallback(
    (attribute: string) => ({
      sortSearchKey: "sortAttribute" as const,
      sortParamValue: attribute,
    }),
    []
  );

  const handleFilterChange = useCallback(
    (key: string, value: string[]) => {
      updateSearchParamsByKey({ [key]: value, page: "0" });
    },
    [updateSearchParamsByKey]
  );

  const hasFilters =
    clusterNameFilter.length > 0 ||
    workspaceFilter.length > 0 ||
    computeTypeFilter.length > 0 ||
    enablePhotonFilter !== "";

  const headers: TableHeaderType<ClusterSummary>[] = useMemo(
    () => [
      {
        id: 1,
        uid: "cluster_name",
        label: "Cluster Name",
        width: "16%",
        filter: buildClusterFiltersApiConfig(
          "Cluster",
          clusterNameFilter,
          "clusterName",
          "cluster_names",
          workspaceId,
          computeTypeFilter,
          handleFilterChange
        ),
        render: (item: ClusterSummary) => (
          <div>
            <Typography className="al-text-brand" weight="medium">
              {item.cluster_name}
            </Typography>
            <Typography size="xs" className="al-text-gray-500">
              {item.cluster_id}
            </Typography>
          </div>
        ),
      },
      {
        id: 2,
        uid: "workspace",
        label: "Workspace",
        width: "10%",
        filter: buildClusterFiltersApiConfig(
          "Workspace",
          workspaceFilter,
          "workspace",
          "workspaces",
          workspaceId,
          computeTypeFilter,
          handleFilterChange
        ),
        render: (item: ClusterSummary) => (
          <Typography>{item.workspace_name || item.workspace_id}</Typography>
        ),
      },
      {
        id: 3,
        uid: "compute_type",
        label: "Type",
        width: "12%",
        filter: buildStaticFilterConfig(
          "Type",
          COMPUTE_TYPE_FILTER_OPTIONS,
          computeTypeFilter,
          "computeType",
          handleFilterChange
        ),
        render: (item: ClusterSummary) => (
          <Badge variant="outline">{getComputeTypeLabel(item.compute_type)}</Badge>
        ),
      },
      {
        id: 11,
        uid: "enable_photon",
        label: "Photon Enabled",
        width: "8%",
        tooltipInfo:
          "Whether the cluster is configured to run Photon. " +
          "Derived from the Databricks runtime version.",
        // Filterable but not sortable. The cost mart (dbx_cluster_daily_metrics)
        // doesn't carry the runtime version, so the backend resolves the Photon
        // filter by joining back to the inventory once per request; sorting by
        // it inline would still require a mart migration, so it stays unsorted.
        filter: {
          filterType: FilterType.DROPDOWN_SINGLE,
          searchKey: "enablePhoton",
          label: "Photon Enabled",
          options: PHOTON_FILTER_OPTIONS,
          value: enablePhotonFilter,
        },
        render: (item: ClusterSummary) => {
          if (item.enable_photon === null || item.enable_photon === undefined) {
            return <Typography>NA</Typography>;
          }
          return (
            <Badge
              variant="outline"
              interactive={false}
              className={cn(
                "al-border-transparent",
                item.enable_photon
                  ? "al-bg-green-100 !al-text-green-500"
                  : "al-bg-red-100 !al-text-action-red"
              )}
            >
              {item.enable_photon ? "Y" : "N"}
            </Badge>
          );
        },
      },
      {
        id: 4,
        uid: "total_cost",
        label: "Total Cost",
        width: "8%",
        sort: buildSortConfig("total_cost"),
        render: (item: ClusterSummary) => (
          <Typography size="sm" className="al-font-medium al-text-brand">
            {formatCost(item.total_cost, currency)}
          </Typography>
        ),
      },
      {
        id: 5,
        uid: "total_dbus",
        label: "DBUs",
        width: "7%",
        sort: buildSortConfig("total_dbus"),
        render: (item: ClusterSummary) => (
          <Typography size="sm" className="al-font-medium">
            {formatNumber(item.total_dbus, { maximumFractionDigits: 1 })}
          </Typography>
        ),
      },
      {
        id: 6,
        uid: "driver_node_type",
        label: "Driver Node",
        width: "11%",
        render: (item: ClusterSummary) => (
          <Typography size="sm">{item.driver_node_type || "-"}</Typography>
        ),
      },
      {
        id: 7,
        uid: "worker_node_type",
        label: "Worker Node",
        width: "11%",
        render: (item: ClusterSummary) => (
          <Typography size="sm">{item.worker_node_type || "-"}</Typography>
        ),
      },
      {
        id: 8,
        uid: "workers",
        label: "Workers",
        width: "7%",
        render: (item: ClusterSummary) =>
          item.min_autoscale_workers !== null && item.max_autoscale_workers !== null ? (
            <Typography>
              {item.min_autoscale_workers} - {item.max_autoscale_workers}
            </Typography>
          ) : (
            <Typography>{item.worker_count ?? "-"}</Typography>
          ),
      },
      {
        id: 9,
        uid: "avg_cpu",
        label: "Avg CPU",
        width: "9%",
        sort: buildSortConfig("avg_cpu"),
        render: (item: ClusterSummary) => (
          <Stack direction="column" gap="xs">
            <Typography size="xs" className="al-text-gray-500">
              {`${Math.round(item.avg_cpu)}%`}
            </Typography>
            <Progress
              value={item.avg_cpu}
              className="al-h-2 al-w-20 al-border al-border-gray-300"
            />
          </Stack>
        ),
      },
      {
        id: 10,
        uid: "avg_memory",
        label: "Avg Memory",
        width: "9%",
        sort: buildSortConfig("avg_memory"),
        render: (item: ClusterSummary) => (
          <Stack direction="column" gap="xs">
            <Typography size="xs" className="al-text-gray-500">
              {`${Math.round(item.avg_memory)}%`}
            </Typography>
            <Progress
              value={item.avg_memory}
              className="al-h-2 al-w-20 al-border al-border-gray-300"
            />
          </Stack>
        ),
      },
    ],
    [
      currency,
      clusterNameFilter,
      workspaceFilter,
      computeTypeFilter,
      enablePhotonFilter,
      workspaceId,
      buildSortConfig,
      handleFilterChange,
    ]
  );

  return {
    headers,
    sortAttribute,
    sortOrder,
    hasFilters,
    clusterNameFilter,
    workspaceFilter,
    computeTypeFilter,
    enablePhotonFilter,
  };
};

export default useClusterListHeaders;
